import { useMemo } from 'react'
import { toast } from 'react-toastify'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import handleCallbackAction, { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CopyAgencyDataModal from '@modals/CopyAgencyDataModal'
import CreateContractModal from '@modals/CreateContractModal'
import CreateOrEditAgencyContactModal from '@modals/CreateOrEditAgencyContactModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageAgencyEnabledStatusModal from '@modals/ManageAgencyEnabledStatusModal'
import ManageAgencyFeaturesModal from '@modals/ManageAgencyFeaturesModal'
import ManageAgencyOptionsModal from '@modals/ManageAgencyOptionsModal'
import ManageAgencyPricingPlanModal from '@modals/ManageAgencyPricingPlanModal'
import ManageAgencySandboxStatusModal from '@modals/ManageAgencySandboxStatusModal'
import ManageAgencySubscriptionModal from '@modals/ManageAgencySubscriptionModal'
import ManageAgencyTemplatesModal from '@modals/ManageAgencyTemplatesModal'
import ManageEntitySupportTeamModal from '@modals/ManageEntitySupportTeamModal'

import {
  ActivityModel, AgencyModel, CommentModel, ContactModel,
} from '@models/types'

import { EntityOptions } from '@redux/modules/module'

import PageContent from './components/PageContent'

type DefaultState = {
  showCopyAgencyDataModal: boolean,
  showCreateContractModal: boolean,
  showCreateOrEditAgencyContactModal: boolean,
  showManageActivityModal: boolean,
  showManageAgencyEnabledStatusModal: boolean,
  showManageAgencyFeaturesModal: boolean,
  showManageAgencyOptionsModal: boolean,
  showManageAgencyPricingPlanModal: boolean,
  showManageAgencySandboxStatusModal: boolean,
  showManageAgencySubscriptionModal: boolean,
  showManageAgencyTemplatesModal: boolean,
  showManageEntitySupportTeamModal: boolean,
}

const defaultState = {
  showCopyAgencyDataModal: false,
  showCreateContractModal: false,
  showCreateOrEditAgencyContactModal: false,
  showManageActivityModal: false,
  showManageAgencyEnabledStatusModal: false,
  showManageAgencyOptionsModal: false,
  showManageAgencyFeaturesModal: false,
  showManageAgencyPricingPlanModal: false,
  showManageAgencySandboxStatusModal: false,
  showManageAgencySubscriptionModal: false,
  showManageAgencyTemplatesModal: false,
  showManageEntitySupportTeamModal: false,
}

type SetState = (state: Partial<DefaultState>) => void

const createComment = (
  params: Partial<CommentModel>,
  createFn: (createParams: Partial<CommentModel>, requestOptions: EntityOptions) => Promise<any>,
) => {
  createFn(params, { ...defaultRequestOptions.comment, ...defaultRequestOptions.conversation })
    .then(({ success, errors }) => {
      if (!success && errors){
        toast.warn(errors[0])
      }
    })
}

const updateAgencyEnabled = (
  commentParams: Partial<CommentModel>,
  agencyParams: Partial<AgencyModel>,
  createCommentFn: (params: Partial<CommentModel>) => Promise<any>,
  updateAgencyFn: (params: Partial<AgencyModel>) => Promise<any>,
  setState: SetState,
) => {
  updateAgencyFn(agencyParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    toast(`Agency ${agencyParams.active ? 'Enabled' : 'Disabled'} Successfully`)
    setState({ showManageAgencyEnabledStatusModal: false })

    createComment(commentParams, createCommentFn)
  })
}

type UpdateAgencyTemplatePermissions = {
  permissionAction: 'assign' | 'unassign',
  serviceId: number,
}

const updateAgencyTemplatePermissions = (
  permissionAction: Partial<UpdateAgencyTemplatePermissions>,
  serviceId: Partial<UpdateAgencyTemplatePermissions>,
  updateFn: (
    arg: Partial<UpdateAgencyTemplatePermissions>,
    argTwo: Partial<UpdateAgencyTemplatePermissions>) => Promise<any>,
) => (
  updateFn(permissionAction, serviceId).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    toast('Templates Updated Successfully')
  })
)

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CopyAgencyDataModal: {
      closeModal: () => setState({ showCopyAgencyDataModal: false }),
      copyAgencyData: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateContractModal: {
      closeModal: () => setState({ showCreateContractModal: false }),
      createContract: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAgencyContactModal: {
      closeModal: () => setState({ showCreateOrEditAgencyContactModal: false }),
      createAgencyContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    ManageAgencyEnabledStatusModal: {
      closeModal: () => setState({ showManageAgencyEnabledStatusModal: false }),
      updateAgencyEnabled: (
        commentParams: Partial<CommentModel>,
        agencyParams: Partial<AgencyModel>,
        createCommentFn: (params: Partial<CommentModel>) => Promise<any>,
        updateAgencyFn: (params: Partial<AgencyModel>) => Promise<any>,
      ) => (
        updateAgencyEnabled(commentParams, agencyParams, createCommentFn, updateAgencyFn, setState)
      ),
    },
    ManageAgencyFeaturesModal: {
      closeModal: () => setState({ showManageAgencyFeaturesModal: false }),
      // @ts-expect-error TS7006
      updateAgency: payload => handleCallbackAction(payload),
    },
    ManageAgencyOptionsModal: {
      closeModal: () => setState({ showManageAgencyOptionsModal: false }),
      // @ts-expect-error TS7006
      updateAgency: payload => handleCallbackAction(payload),
    },
    ManageAgencySubscriptionModal: {
      closeModal: () => setState({ showManageAgencySubscriptionModal: false }),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageAgencySandboxStatusModal: {
      closeModal: () => setState({ showManageAgencySandboxStatusModal: false }),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageAgencyTemplatesModal: {
      closeModal: () => setState({ showManageAgencyTemplatesModal: false }),
      updateAgencyTemplatePermissions,
    },
    ManageEntitySupportTeamModal: {
      closeModal: () => setState({ showManageEntitySupportTeamModal: false }),
      updateEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageAgencyPricingPlanModal: {
      closeModal: () => setState({ showManageAgencyPricingPlanModal: false }),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCopyAgencyDataModal,
    showCreateContractModal,
    showCreateOrEditAgencyContactModal,
    showManageActivityModal,
    showManageAgencyEnabledStatusModal,
    showManageAgencyFeaturesModal,
    showManageAgencyOptionsModal,
    showManageAgencyPricingPlanModal,
    showManageAgencySandboxStatusModal,
    showManageAgencySubscriptionModal,
    showManageAgencyTemplatesModal,
    showManageEntitySupportTeamModal,
  } = state

  const { agencyId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      createComment,
      deleteAgencyContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteContract: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      exportAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showCopyAgencyDataModal: (payload: AgencyModel) => {
        setModalData('CopyAgencyDataModal', payload)
        setState({ showCopyAgencyDataModal: true })
      },
      showCreateContractModal: (payload: ContactModel) => {
        setModalData('CreateContractModal', payload)
        setState({ showCreateContractModal: true })
      },
      showCreateOrEditAgencyContactModal: (payload: AgencyModel) => {
        setModalData('CreateOrEditAgencyContactModal', payload)
        setState({ showCreateOrEditAgencyContactModal: true })
      },
      showManageActivityModal: (payload: ActivityModel) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManageAgencyEnabledStatusModal: (payload: AgencyModel) => {
        setModalData('ManageAgencyEnabledStatusModal', payload)
        setState({ showManageAgencyEnabledStatusModal: true })
      },
      // @ts-expect-error TS7006
      showManageAgencyFeaturesModal: (payload) => {
        setModalData('ManageAgencyFeaturesModal', payload)
        setState({ showManageAgencyFeaturesModal: true })
      },
      // @ts-expect-error TS7006
      showManageAgencyOptionsModal: (payload) => {
        setModalData('ManageAgencyOptionsModal', payload)
        setState({ showManageAgencyOptionsModal: true })
      },
      // @ts-expect-error TS7006
      showManageAgencyPricingPlanModal: (payload) => {
        setModalData('ManageAgencyPricingPlanModal', payload)
        setState({ showManageAgencyPricingPlanModal: true })
      },
      showManageAgencySandboxStatusModal: (payload: AgencyModel) => {
        setModalData('ManageAgencySandboxStatusModal', payload)
        setState({ showManageAgencySandboxStatusModal: true })
      },
      showManageAgencySubscriptionModal: (payload: AgencyModel) => {
        setModalData('ManageAgencySubscriptionModal', payload)
        setState({ showManageAgencySubscriptionModal: true })
      },
      showManageAgencyTemplatesModal: (payload: AgencyModel) => {
        setModalData('ManageAgencyTemplatesModal', payload)
        setState({ showManageAgencyTemplatesModal: true })
      },
      showManageEntitySupportTeamModal: (payload: {id: number, type: 'User' | 'Agency'}) => {
        setModalData('ManageEntitySupportTeamModal', payload)
        setState({ showManageEntitySupportTeamModal: true })
      },
      toggleAgencyLock: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent agencyId={agencyId} />

        <CopyAgencyDataModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CopyAgencyDataModal', setState)}
          showModal={showCopyAgencyDataModal}
        />

        <CreateContractModal
          callbacks={callbacks('CreateContractModal', setState)}
          showModal={showCreateContractModal}
        />

        <CreateOrEditAgencyContactModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAgencyContactModal', setState)}
          showModal={showCreateOrEditAgencyContactModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <ManageAgencyEnabledStatusModal
          callbacks={callbacks('ManageAgencyEnabledStatusModal', setState)}
          showModal={showManageAgencyEnabledStatusModal}
        />

        <ManageAgencyFeaturesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageAgencyFeaturesModal', setState)}
          showModal={showManageAgencyFeaturesModal}
        />

        <ManageAgencyOptionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageAgencyOptionsModal', setState)}
          showModal={showManageAgencyOptionsModal}
        />

        <ManageAgencyPricingPlanModal
          callbacks={callbacks('ManageAgencyPricingPlanModal', setState)}
          showModal={showManageAgencyPricingPlanModal}
        />

        <ManageAgencySandboxStatusModal
          callbacks={callbacks('ManageAgencySandboxStatusModal', setState)}
          showModal={showManageAgencySandboxStatusModal}
        />

        <ManageAgencySubscriptionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageAgencySubscriptionModal', setState)}
          showModal={showManageAgencySubscriptionModal}
        />

        <ManageAgencyTemplatesModal
          callbacks={callbacks('ManageAgencyTemplatesModal', setState)}
          showModal={showManageAgencyTemplatesModal}
        />

        <ManageEntitySupportTeamModal
          callbacks={callbacks('ManageEntitySupportTeamModal', setState)}
          showModal={showManageEntitySupportTeamModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
